import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../services/api';
import { Shield, Sparkles, Paintbrush, ArrowRight, ArrowLeft } from 'lucide-react';

export default function FranchiseOnboard() {
    const { setAuth } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();

    const [form, setForm] = useState({
        name: '',
        slug: '',
        email: '',
        password: '',
        defaultPlan: 'Starter',
        primaryColor: '#ec2626',
        secondaryColor: '#ffffff'
    });
    const [loading, setLoading] = useState(false);
    const [registeredUrl, setRegisteredUrl] = useState('');

    const validatePassword = (password) => {
        if (password.length < 8) return 'Password must be at least 8 characters long';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
        if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
        if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
        if (!/[^A-Za-z0-9]/.test(password)) return 'Password must contain at least one special character';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const passwordError = validatePassword(form.password);
        if (passwordError) {
            return toast.error(passwordError);
        }

        setLoading(true);
        try {
            const res = await api.post('/api/franchises/onboard', form);
            // Log in using the returned token
            setAuth(res.data.token, res.data.user);
            const franchiseUrl = `${window.location.origin}/?restaurant=${res.data.franchise.slug}`;
            setRegisteredUrl(franchiseUrl);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || 'Failed to onboard franchise');
        } finally {
            setLoading(false);
        }
    };

    if (registeredUrl) {
        return (
            <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 font-sans relative overflow-hidden">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
                <div className="bg-slate-900 border border-slate-800 p-8 sm:p-10 rounded-3xl max-w-md w-full shadow-2xl space-y-6 text-center animate-scale-up relative z-10">
                    <div className="w-16 h-16 bg-red-600/20 text-red-500 rounded-3xl flex items-center justify-center mx-auto border border-red-500/25">
                        <Sparkles className="w-8 h-8 animate-pulse" />
                    </div>
                    <div>
                        <h2 className="text-3xl font-black text-white">Franchise Created!</h2>
                        <p className="text-slate-400 text-sm mt-2">Your brand workspace is now active. Copy your public storefront portal URL below.</p>
                    </div>

                    <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl flex items-center justify-between gap-3">
                        <span className="text-xs text-slate-350 select-all break-all text-left font-mono">{registeredUrl}</span>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(registeredUrl);
                                toast.success('URL copied to clipboard!');
                            }}
                            className="bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs px-3 py-1.5 rounded-lg font-bold transition-colors flex-shrink-0"
                        >
                            Copy
                        </button>
                    </div>

                    <button
                        onClick={() => navigate('/admin')}
                        className="w-full bg-red-600 hover:bg-red-500 text-white py-5 px-8 rounded-2xl font-black text-base uppercase tracking-widest transition-all duration-200 flex items-center justify-center gap-3 shadow-2xl shadow-red-600/30 hover:shadow-red-600/50 border border-red-500/30 transform active:scale-95"
                    >
                        <span>Go to Admin Dashboard</span>
                        <ArrowRight className="w-5 h-5" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-950 text-white flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans relative overflow-hidden">
            {/* Background Gradients */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

            <div className="sm:mx-auto sm:w-full sm:max-w-xl relative z-10 text-center space-y-4">
                <Link to="/" className="inline-flex items-center text-xs text-slate-400 hover:text-white transition-colors gap-1.5 mb-2">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
                </Link>
                <div className="w-16 h-16 bg-red-600/20 text-red-500 rounded-3xl flex items-center justify-center mx-auto border border-red-500/25">
                    <Sparkles className="w-8 h-8" />
                </div>
                <h2 className="text-4xl font-black tracking-tight text-white">Create Your Brand Franchise</h2>
                <p className="text-slate-400 text-sm max-w-sm mx-auto">Onboard your food brand on Aaharam Cloud, configure unified branding, and manage multiple outlets.</p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl relative z-10">
                <div className="bg-slate-900 border border-slate-800/80 p-8 sm:p-10 rounded-3xl shadow-2xl space-y-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Brand / Franchise Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Aaharam Delights"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-red-500 outline-none text-white transition-all placeholder-slate-600"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Brand URL Slug</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. aaharam-delights"
                                    value={form.slug}
                                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-red-500 outline-none text-white transition-all placeholder-slate-600 font-mono"
                                />
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Primary Email (Owner)</label>
                                <input
                                    type="email"
                                    required
                                    placeholder="owner@mybrand.com"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-red-500 outline-none text-white transition-all placeholder-slate-600"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Franchise Login Password</label>
                                <input
                                    type="password"
                                    required
                                    placeholder="••••••••"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 text-sm font-medium focus:ring-2 focus:ring-red-500 outline-none text-white transition-all placeholder-slate-600"
                                />
                            </div>
                        </div>

                        {/* Password Requirement Checklist & Strength Meter */}
                        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-2">
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Security Requirements</p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                                <div className={`text-[11px] flex items-center ${form.password.length >= 8 ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${form.password.length >= 8 ? 'bg-emerald-400' : 'bg-slate-700'}`} />
                                    8+ characters
                                </div>
                                <div className={`text-[11px] flex items-center ${/[A-Z]/.test(form.password) ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[A-Z]/.test(form.password) ? 'bg-emerald-400' : 'bg-slate-700'}`} />
                                    Uppercase
                                </div>
                                <div className={`text-[11px] flex items-center ${/[a-z]/.test(form.password) ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[a-z]/.test(form.password) ? 'bg-emerald-400' : 'bg-slate-700'}`} />
                                    Lowercase
                                </div>
                                <div className={`text-[11px] flex items-center ${/[0-9]/.test(form.password) ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[0-9]/.test(form.password) ? 'bg-emerald-400' : 'bg-slate-700'}`} />
                                    Number
                                </div>
                                <div className={`text-[11px] flex items-center ${/[^A-Za-z0-9]/.test(form.password) ? 'text-emerald-400 font-semibold' : 'text-slate-500'}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${/[^A-Za-z0-9]/.test(form.password) ? 'bg-emerald-400' : 'bg-slate-700'}`} />
                                    Special Char
                                </div>
                            </div>
                            
                            {/* Strength Bar */}
                            <div className="mt-3 h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full transition-all duration-300 ${
                                        [
                                            form.password.length >= 8,
                                            /[A-Z]/.test(form.password),
                                            /[a-z]/.test(form.password),
                                            /[0-9]/.test(form.password),
                                            /[^A-Za-z0-9]/.test(form.password)
                                        ].filter(Boolean).length <= 2 ? 'bg-rose-500 w-1/3' : 
                                        [
                                            form.password.length >= 8,
                                            /[A-Z]/.test(form.password),
                                            /[a-z]/.test(form.password),
                                            /[0-9]/.test(form.password),
                                            /[^A-Za-z0-9]/.test(form.password)
                                        ].filter(Boolean).length <= 4 ? 'bg-amber-400 w-2/3' : 'bg-emerald-400 w-full'
                                    }`}
                                />
                            </div>
                        </div>

                        {/* Select Platform Subscription Plan */}
                        <div className="space-y-3">
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">Select Default Plan for Branches</label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {[
                                    { id: 'Starter', price: '₹2,999/mo', desc: 'Website & Core POS' },
                                    { id: 'Professional', price: '₹6,999/mo', desc: 'Adds KDS & Chatbot' },
                                    { id: 'Premium', price: '₹9,999/mo', desc: 'KDS, Delivery Apps & WA' }
                                ].map((plan) => (
                                    <div
                                        key={plan.id}
                                        onClick={() => setForm({ ...form, defaultPlan: plan.id })}
                                        className={`p-4 rounded-2xl border cursor-pointer transition-all ${
                                            form.defaultPlan === plan.id
                                                ? 'bg-red-950/40 border-red-500 ring-2 ring-red-500/30'
                                                : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-extrabold text-sm text-white">{plan.id}</span>
                                            <span className="text-[10px] font-mono font-bold text-red-400">{plan.price}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 leading-tight">{plan.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Brand theme picker */}
                        <div className="p-5 bg-slate-950 border border-slate-800/80 rounded-2xl space-y-4">
                            <div className="flex items-center gap-2 text-slate-300 font-bold text-sm">
                                <Paintbrush className="w-4 h-4 text-red-500" />
                                <span>Customize Brand Aesthetics</span>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Primary Theme Color</label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={form.primaryColor}
                                            onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                                            className="w-10 h-10 border-0 rounded-lg cursor-pointer bg-transparent"
                                        />
                                        <span className="text-xs font-mono text-slate-400">{form.primaryColor}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Secondary Accent Color</label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="color"
                                            value={form.secondaryColor}
                                            onChange={(e) => setForm({ ...form, secondaryColor: e.target.value })}
                                            className="w-10 h-10 border-0 rounded-lg cursor-pointer bg-transparent"
                                        />
                                        <span className="text-xs font-mono text-slate-400">{form.secondaryColor}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 text-white py-5 px-8 rounded-2xl font-black text-base uppercase tracking-widest transition-all duration-200 flex items-center justify-center gap-3 shadow-2xl shadow-red-600/40 hover:shadow-red-600/60 border border-red-400/30 transform active:scale-95"
                        >
                            <span>{loading ? 'Creating Franchise Brand...' : 'Register Franchise Brand'}</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
